import React from "react";
import "./index.css";
import COLORS from "./lib/colors";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ConfirmProvider } from "./components/ConfirmDialog";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import { Loader2 } from "lucide-react";

import ClientDashboard from "./pages/client/ClientDashboard";
import ClientProjects from "./pages/client/ClientProjects";
import ClientProjectDetail from "./pages/client/ClientProjectDetail";
import ClientIdentity from "./pages/client/ClientIdentity";
import ClientHelp from "./pages/client/ClientHelp";
import ClientAccount from "./pages/client/ClientAccount";
import ClientCalendar from "./pages/client/ClientCalendar";

import CollabDashboard from "./pages/collab/CollabDashboard";
import MissionDetail from "./pages/collab/MissionDetail";
import CollabMissions from "./pages/collab/CollabMissions";
import CollabCalendar from "./pages/collab/CollabCalendar";
import CollabClients from "./pages/collab/CollabClients";
import CollabClientDetail from "./pages/collab/CollabClientDetail";
import CollabProjectDetail from "./pages/collab/CollabProjectDetail";

import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminClients from "./pages/admin/AdminClients";
import AdminClientDetail from "./pages/admin/AdminClientDetail";
import AdminClientModule from "./pages/admin/AdminClientModule";
import AdminEntities from "./pages/admin/AdminEntities";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminContactDetail from "./pages/admin/AdminContactDetail";
import AdminProjects from "./pages/admin/AdminProjects";
import AdminModules from "./pages/admin/AdminModules";
import AdminModuleDetail from "./pages/admin/AdminModuleDetail";
import AdminModuleMissions from "./pages/admin/AdminModuleMissions";
import AdminProjectDetail from "./pages/admin/AdminProjectDetail";
import AdminIdentity from "./pages/admin/AdminIdentity";
import AdminAccounting from "./pages/admin/AdminAccounting";
import AdminCurrencies from "./pages/admin/AdminCurrencies";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminHelp from "./pages/admin/AdminHelp";
import AdminCalendar from "./pages/admin/AdminCalendar";
import AdminCollaborators from "./pages/admin/AdminCollaborators";
import AdminCollaboratorDetail from "./pages/admin/AdminCollaboratorDetail";
import AdminMissions from "./pages/admin/AdminMissions";
import AdminMissionDetail from "./pages/admin/AdminMissionDetail";
import AdminNotify from "./pages/admin/AdminNotify";
import AdminEmailAutomations from "./pages/admin/AdminEmailAutomations";
import AdminEmailLog from "./pages/admin/AdminEmailLog";
import AdminTaskAutomations from "./pages/admin/AdminTaskAutomations";
import AdminAutomationRules from "./pages/admin/AdminAutomationRules";
import AdminAssistant from "./pages/admin/AdminAssistant";
import ProjectWorkflows from "./pages/admin/ProjectWorkflows";
import ProjectWorkflowEditor from "./pages/admin/ProjectWorkflowEditor";
import WorkflowTemplates from "./pages/admin/WorkflowTemplates";
import FormTemplates from "./pages/admin/FormTemplates";
import FormTemplateEditor from "./pages/admin/FormTemplateEditor";

import ProfilePage from "./pages/ProfilePage";
import SettingsPage from "./pages/SettingsPage";
import ClientSettings from "./pages/client/ClientSettings";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import OnboardingWizard from "./pages/OnboardingWizard";

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center beige-texture"><Loader2 size={32} className="animate-spin text-marine" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (user.role === "client" && !user.onboarding?.completed) return <OnboardingWizard />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

const Home = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <AdminDashboard />;
  if (user.role === "collaborator") return <CollabDashboard />;
  return <ClientDashboard />;
};

const SettingsRoute = () => {
  const { user } = useAuth();
  return user?.role === "client" ? <ClientSettings /> : <SettingsPage />;
};

const CalendarRoute = () => {
  const { user } = useAuth();
  return user?.role === "collaborator" ? <CollabCalendar /> : <ClientCalendar />;
};

function App() {
  return (
    <AuthProvider>
     <ConfirmProvider>
      <Toaster position="top-right" toastOptions={{
        style: { background: "#fff", color: COLORS.marine, border: "1px solid rgba(51,51,138,0.12)", borderRadius: "12px", boxShadow: "0 12px 32px rgba(26,26,46,.10)", fontFamily: "Manrope, sans-serif", fontSize: "13px" },
      }} />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
          <Route path="/" element={<Protected><Home /></Protected>} />

          <Route path="/projets" element={<Protected roles={["client"]}><ClientProjects /></Protected>} />
          <Route path="/projets/:id" element={<Protected roles={["client"]}><ClientProjectDetail /></Protected>} />
          <Route path="/identite" element={<Protected roles={["client"]}><ClientIdentity /></Protected>} />
          <Route path="/aide" element={<Protected roles={["client", "collaborator"]}><ClientHelp /></Protected>} />
          <Route path="/compte" element={<Protected roles={["client"]}><ClientAccount /></Protected>} />
          <Route path="/calendrier" element={<Protected roles={["client", "collaborator"]}><CalendarRoute /></Protected>} />

          <Route path="/missions" element={<Protected roles={["collaborator"]}><CollabMissions /></Protected>} />
          <Route path="/missions/:id" element={<Protected roles={["collaborator"]}><MissionDetail /></Protected>} />
          <Route path="/clients" element={<Protected roles={["collaborator"]}><CollabClients /></Protected>} />
          <Route path="/clients/:cid" element={<Protected roles={["collaborator"]}><CollabClientDetail /></Protected>} />
          <Route path="/clients/:cid/projets/:pid" element={<Protected roles={["collaborator"]}><CollabProjectDetail /></Protected>} />

          <Route path="/admin/clients" element={<Protected roles={["admin"]}><AdminClients /></Protected>} />
          <Route path="/admin/clients/:id" element={<Protected roles={["admin"]}><AdminClientDetail /></Protected>} />
          <Route path="/admin/clients/:id/modules/:key" element={<Protected roles={["admin"]}><AdminClientModule /></Protected>} />
          <Route path="/admin/entites" element={<Protected roles={["admin"]}><AdminEntities /></Protected>} />
          <Route path="/admin/contacts" element={<Protected roles={["admin"]}><AdminContacts /></Protected>} />
          <Route path="/admin/contacts/:id" element={<Protected roles={["admin"]}><AdminContactDetail /></Protected>} />
          <Route path="/admin/projets" element={<Protected roles={["admin"]}><AdminProjects /></Protected>} />
          <Route path="/admin/modules" element={<Protected roles={["admin"]}><AdminModules /></Protected>} />
          <Route path="/admin/modules/:key" element={<Protected roles={["admin"]}><AdminModuleDetail /></Protected>} />
          <Route path="/admin/modules/:key/missions" element={<Protected roles={["admin"]}><AdminModuleMissions /></Protected>} />
          <Route path="/admin/projets/:id" element={<Protected roles={["admin"]}><AdminProjectDetail /></Protected>} />
          <Route path="/admin/identite" element={<Protected roles={["admin"]}><AdminIdentity /></Protected>} />
          <Route path="/admin/aide" element={<Protected roles={["admin"]}><AdminHelp /></Protected>} />
          <Route path="/admin/calendrier" element={<Protected roles={["admin"]}><AdminCalendar /></Protected>} />
          <Route path="/admin/collaborateurs" element={<Protected roles={["admin"]}><AdminCollaborators /></Protected>} />
          <Route path="/admin/collaborateurs/:id" element={<Protected roles={["admin"]}><AdminCollaboratorDetail /></Protected>} />
          <Route path="/admin/missions" element={<Protected roles={["admin"]}><AdminMissions /></Protected>} />
          <Route path="/admin/missions/:id" element={<Protected roles={["admin"]}><AdminMissionDetail /></Protected>} />
          <Route path="/admin/comptabilite" element={<Protected roles={["admin"]}><AdminAccounting /></Protected>} />
          <Route path="/admin/comptabilite/devises" element={<Protected roles={["admin"]}><AdminCurrencies /></Protected>} />
          <Route path="/admin/comptabilite/factures" element={<Protected roles={["admin"]}><AdminInvoices /></Protected>} />
          <Route path="/admin/notifications" element={<Protected roles={["admin"]}><AdminNotify /></Protected>} />
          <Route path="/admin/automations" element={<Protected roles={["admin"]}><AdminEmailAutomations /></Protected>} />
          <Route path="/admin/journal-emails" element={<Protected roles={["admin"]}><AdminEmailLog /></Protected>} />
          <Route path="/admin/automations-taches" element={<Protected roles={["admin"]}><AdminTaskAutomations /></Protected>} />
          <Route path="/admin/automations-workflows-prestation" element={<Protected roles={["admin"]}><ProjectWorkflows /></Protected>} />
          <Route path="/admin/automations-workflows-prestation/:id" element={<Protected roles={["admin"]}><ProjectWorkflowEditor /></Protected>} />
          <Route path="/admin/automations-workflows-mission" element={<Protected roles={["admin"]}><WorkflowTemplates /></Protected>} />
          <Route path="/admin/automations-formulaires" element={<Protected roles={["admin"]}><FormTemplates /></Protected>} />
          <Route path="/admin/automations-formulaires/:id" element={<Protected roles={["admin"]}><FormTemplateEditor /></Protected>} />
          <Route path="/admin/automations-regles" element={<Protected roles={["admin"]}><AdminAutomationRules /></Protected>} />
          <Route path="/admin/assistant" element={<Protected roles={["admin"]}><AdminAssistant /></Protected>} />

          <Route path="/profil" element={<Protected roles={["admin","collaborator"]}><ProfilePage /></Protected>} />
          <Route path="/parametres" element={<Protected><SettingsRoute /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
     </ConfirmProvider>
    </AuthProvider>
  );
}
export default App;
